import { Badge } from "@components/ui/Badge"
import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatDate, formatGrade } from "@lib/intl"
import { TYPE_ICONS, DEFAULT_TYPE_ICON, materialTypeLabel } from "@lib/materials"
import { memo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { effectiveStatus } from "./statusVisuals"

import { MATERIAL_ORIGIN } from "@/constants/materials"
import type { Material } from "@/types"

/**
 * One material, one line (PRD-59).
 *
 * The `curso` and `archivo` lanes have nothing for the professor to decide, so
 * they do not get cards. A card is a container for a decision — its status
 * badge, its quality strip, its grading bar, its kebab and its full-width
 * primary button all exist to support one. Rendering forty finished materials
 * that way filled three screens with buttons that said "Ver detalle".
 *
 * A row says the two things she is actually scanning for — which material, and
 * when it happens — and hands everything else to the detail page, which owns
 * every action anyway (send now, cancel, duplicate, delete). One click, not a
 * menu on every line.
 *
 * The one exception is `action` (PRD-62): an archived material's only verb,
 * "Restaurar", sits beside the link rather than inside it — a button nested in
 * an anchor has no clean keyboard or screen-reader story.
 */
export const MaterialRow = memo(function MaterialRow({
  material,
  action,
}: {
  material: Material
  action?: ReactNode
}) {
  const { t } = useTranslation("materials")
  const scale = useGradeScale()
  const status = effectiveStatus(material)
  const TypeIcon = TYPE_ICONS[material.material_type] ?? DEFAULT_TYPE_ICON

  // What this row is waiting on, or what became of it. One phrase, never two:
  // a row that needs two sentences belongs in the `atiende` lane as a card.
  const when = (() => {
    // Archived trumps "enviado": the date she needs is the one it stopped counting.
    if (material.archived_at) {
      return t("archived.row.on", { date: formatDate(material.archived_at, "PP") })
    }
    if (status === "procesando") {
      return t("row.generating", {
        ready: material.assignments_ready,
        total: material.assignments_total,
      })
    }
    if (status === "por_enviar" || status === "programado") {
      return material.send_date
        ? t("row.sends_on", { date: formatDate(material.send_date, "PP") })
        : t("row.sends_undated")
    }
    if (status === "enviado") {
      return material.sent_at
        ? t("row.sent_on", { date: formatDate(material.sent_at, "PP") })
        : t("row.sent")
    }
    if (status === "cancelado") return t("row.cancelled")
    return t("row.draft")
  })()

  // Second line, only when there is something true to put there. A practice
  // material (`creates_grade_record === false`) has no grades to report, so it
  // says nothing rather than "0/0" (PRD-29).
  const detail = (() => {
    // An archived material's grades count nowhere; its tally would read as live.
    if (material.archived_at) return null
    if (status !== "enviado" || !material.creates_grade_record) return null
    const { graded, total, avg_grade } = material.grading_completion
    if (total === 0) return null
    if (graded === 0) return t("row.none_graded", { count: total })
    return avg_grade === null
      ? t("row.graded", { graded, total })
      : t("row.graded_avg", { graded, total, avg: formatGrade(avg_grade, scale) })
  })()

  return (
    <li
      className={cn(
        "border-hairline hover:bg-inset relative border-b last:border-b-0",
        action && "flex items-center gap-2 pr-3",
      )}
    >
      {/* Stretched link: the row is one target without nesting anything
          interactive inside an anchor (accessibility.md §1). */}
      <Link
        to={`/materiales/${material.id}`}
        className={cn("flex min-h-14 items-center gap-3 px-3 py-2", action && "min-w-0 flex-1")}
      >
        <TypeIcon className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="text-ink block truncate text-sm font-medium">{material.title}</span>
          <span className="text-meta block truncate">
            {material.classroom_name} · {materialTypeLabel(t, material.material_type)}
            {material.origin === MATERIAL_ORIGIN.staff ? <> · {t("origin.staff")}</> : null}
          </span>
        </span>
        {/* Beside an action, the date yields to it on a phone: at 375px the
            title, the date and a 44px button do not fit on one line. */}
        <span className={cn("shrink-0 text-right", action && "hidden sm:block")}>
          {status === "cancelado" ? (
            <Badge variant="neutral">{when}</Badge>
          ) : (
            <span className="text-ink-muted block text-xs font-medium">{when}</span>
          )}
          {detail ? <span className="text-meta block">{detail}</span> : null}
        </span>
      </Link>
      {action}
    </li>
  )
})
